import React from 'react';
import { Loader2, Save, X } from 'lucide-react';
import { META_UNILEVER_EDITABLE_COLUMNS, MetaUnileverRow } from './types';
import {
  createEmptyRow,
  finalizeMetaValue,
  finalizeMonthValue,
  finalizeYearValue,
  getMonthSelectValue,
  getYearOptions,
  getYearSelectValue,
  MONTH_OPTIONS,
  sanitizeMetaInput,
} from './utils';

const YEAR_OPTIONS = getYearOptions();

type FormModalProps = {
  isOpen: boolean;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (row: MetaUnileverRow) => void | Promise<void>;
};

export const FormModal: React.FC<FormModalProps> = ({
  isOpen,
  loading = false,
  onClose,
  onSubmit,
}) => {
  const [visible, setVisible] = React.useState(false);
  const [form, setForm] = React.useState<MetaUnileverRow>(() => createEmptyRow());

  React.useEffect(() => {
    if (isOpen) {
      setForm(createEmptyRow());
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !loading) onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, loading, onClose]);

  if (!isOpen) return null;

  const handleChange = (key: keyof Omit<MetaUnileverRow, 'guid'>, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    await onSubmit({
      ...form,
      mes: finalizeMonthValue(form.mes),
      ano: finalizeYearValue(form.ano),
      metaHpf: finalizeMetaValue(form.metaHpf),
    });
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center px-4 transition-all duration-200 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
      role="dialog"
      aria-modal="true"
      aria-label="Cadastrar meta Unilever"
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={loading ? undefined : onClose} />

      <form
        onSubmit={handleSubmit}
        className={`relative flex max-h-[90vh] w-full max-w-3xl flex-col rounded-2xl bg-white shadow-2xl transition-all duration-200 ${
          visible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-3'
        }`}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Cadastrar Meta Unilever</h2>
            <p className="mt-0.5 text-xs text-gray-500">Formato de cadastro individual</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded-xl p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid flex-1 gap-4 overflow-y-auto p-6 sm:grid-cols-2">
          {META_UNILEVER_EDITABLE_COLUMNS.map((column) => {
            const rawValue = form[column.key];
            const value = rawValue === null || rawValue === undefined ? '' : String(rawValue);
            const isMonthField = column.type === 'month';
            const isYearField = column.type === 'year';

            if (isMonthField || isYearField) {
              return (
                <label key={column.key} className="sm:col-span-1">
                  <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {column.label}
                  </span>
                  <select
                    value={isMonthField ? getMonthSelectValue(value) : getYearSelectValue(value)}
                    onChange={(event) => handleChange(column.key, event.target.value)}
                    className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-800 outline-none transition-all focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20"
                  >
                    <option value="">-</option>
                    {isMonthField
                      ? MONTH_OPTIONS.map((month) => (
                        <option key={month.value} value={month.value}>{month.label}</option>
                      ))
                      : YEAR_OPTIONS.map((year) => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                  </select>
                </label>
              );
            }

            return (
              <label key={column.key} className={column.type === 'money' ? 'sm:col-span-1' : ''}>
                <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {column.label}
                </span>
                <input
                  type="text"
                  inputMode={column.type === 'money' ? 'decimal' : 'text'}
                  value={value}
                  onChange={(event) => {
                    let nextValue = event.target.value;
                    if (column.type === 'money') nextValue = sanitizeMetaInput(nextValue);
                    handleChange(column.key, nextValue);
                  }}
                  onBlur={(event) => {
                    if (column.type === 'money') {
                      handleChange(column.key, finalizeMetaValue(event.target.value));
                    }
                  }}
                  onKeyDown={(event) => {
                    if (column.type === 'money' && (event.key === ',' || event.keyCode === 188)) {
                      event.preventDefault();
                    }
                  }}
                  className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-800 outline-none transition-all focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20"
                  placeholder={column.type === 'money' ? '0.00' : '-'}
                />
              </label>
            );
          })}
        </div>

        <div className="flex items-center justify-end gap-3 rounded-b-2xl border-t border-gray-200 bg-gray-50 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2 text-sm font-medium text-white shadow-sm shadow-blue-600/20 transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Salvar cadastro
          </button>
        </div>
      </form>
    </div>
  );
};
